import { Box, Chip, styled, Typography } from '@mui/material';
import React from 'react';

import IntellioAdvantageFolderOptionsMenu from '../../components/Folder/FolderOptionsMenu/IntellioAdvantageFolderOptionsMenu';
import { StyledSkeleton } from '../../theme/CustomComponents';
import { Folder } from '../../Types/folder';
import ProjectProgressBar from './ProjectProgressBar';

const ProjectCardContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '220px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.10)',
  padding: '16px 24px',
  cursor: 'pointer',
  minHeight: '230px',
});

const StyledChip = styled(Chip)({
  width: 'fit-content',
  borderRadius: '4px',
  color: 'black',
  fontSize: '14px',
  fontWeight: '700',
});

export const ProjectCardSkeleton: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: 220,
      backgroundColor: '#ffffff',
      borderRadius: 2,
      boxShadow: '0px 4px 4px rgba(0,0,0,0.1)',
      padding: '16px 24px',
      minHeight: 230,
      position: 'relative',
    }}
  >
    {/* Top right menu icon skeleton (simulate with a small circle) */}
    <StyledSkeleton
      variant="circular"
      width={25}
      height={25}
      sx={{ position: 'absolute', top: 16, right: 24 }}
    />

    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Chip */}
      <StyledSkeleton
        variant="rectangular"
        width={100}
        height={28}
        sx={{ borderRadius: 1, mb: 1 }}
      />

      {/* Project name */}
      <StyledSkeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />

      {/* Client name */}
      <StyledSkeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />

      {/* Last Analysis / Discovery block */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Label */}
        <StyledSkeleton variant="text" width="50%" height={24} />

        {/* Date/time */}
        <StyledSkeleton variant="text" width="70%" height={24} />

        {/* Progress bar */}
        <StyledSkeleton variant="rectangular" width="100%" height={16} sx={{ mt: 1, mb: 2 }} />
      </Box>
    </Box>
  </Box>
);

export type ProjectCardProps = {
  folder: Folder;
  handleProjectClick: (projectId: string) => void;
};

const ProjectCard = ({ folder, handleProjectClick }: ProjectCardProps): JSX.Element => {
  const project = folder.project;
  if (!folder || !project) {
    return <></>;
  }

  return (
    <ProjectCardContainer id={`projectid${project.id}`}>
      {folder && (
        <div style={{ position: 'absolute', top: '16px', right: '24px', zIndex: 1 }}>
          <IntellioAdvantageFolderOptionsMenu folder={folder} horizontalOptions />
        </div>
      )}
      <div style={{ flexGrow: '1' }} onClick={() => handleProjectClick(project.id)}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {project.lastAnalysisRunTime && (
            <StyledChip
              label={'ANALYSIS'}
              sx={{ backgroundColor: '#1dd566', color: '#070154', border: '1.5px solid #070154' }}
            />
          )}
          {project.lastDiscoveryRunTime && !project.lastAnalysisRunTime && (
            <StyledChip
              label={'DISCOVERY'}
              sx={{ backgroundColor: '#00a3ff', color: '#070154', border: '1.5px solid #070154' }}
            />
          )}
          {!project.lastDiscoveryRunTime && !project.lastAnalysisRunTime && (
            <StyledChip
              label={'NOT STARTED'}
              sx={{ backgroundColor: '#E8EEF8', color: '#070154', border: '1.5px solid #070154' }}
            />
          )}
          <Typography sx={{ fontSize: '16px', fontWeight: '700', marginTop: '8px' }}>
            {project.name}
          </Typography>
          {project.clientName ? (
            <Typography sx={{ fontSize: '12px', fontWeight: '400' }}>
              <span style={{ fontStyle: 'italic' }}>Client:</span> {project.clientName}
            </Typography>
          ) : (
            <br />
          )}
        </div>
        {project.lastAnalysisRunTime && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <Typography sx={{ fontSize: '16px', fontWeight: '400', fontStyle: 'italic' }}>
                Last Analysis:
              </Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: '400', fontStyle: 'italic' }}>
                {new Date(project.lastAnalysisRunTime).toLocaleString()}
              </Typography>
            </div>
          </div>
        )}
        {project.lastDiscoveryRunTime && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <Typography sx={{ fontSize: '16px', fontWeight: '400', fontStyle: 'italic' }}>
                Last Discovery:
              </Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: '400', fontStyle: 'italic' }}>
                {new Date(project.lastDiscoveryRunTime).toLocaleString()}
              </Typography>
            </div>
            <div style={{ height: '16px', paddingBottom: '20px' }}>
              <ProjectProgressBar projectId={project.id} runId={project.lastDiscoveryRunId} />
            </div>
          </div>
        )}
      </div>
    </ProjectCardContainer>
  );
};

export default ProjectCard;
